<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url('+ bg +')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Cart</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4">
                    <li><router-link to="/index-v2">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">Cart</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100" data-aos="fade-up">
            <div class="container">
                <p v-if="priceNotice" class="mb-6 rounded-lg bg-[#FFF6E5] dark:bg-[#3B3120] px-4 py-3 text-sm text-[#8A5A00] dark:text-[#E8C87A]">
                    {{ priceNotice }}
                </p>

                <div v-if="!items.length" class="max-w-[860px] mx-auto text-center bg-[#FAFAFA] dark:bg-dark-secondary p-10 md:p-16 rounded-xl border border-[#17243026] border-opacity-15">
                    <h3 class="font-semibold leading-none">Your cart is empty</h3>
                    <p class="mt-4 text-base md:text-lg text-paragraph dark:text-white-light">Select a product and its modifiers to start building the order.</p>
                    <div class="mt-8">
                        <router-link to="/shop-v2" class="btn btn-solid" data-text="Browse Products">
                            <span>Browse Products</span>
                        </router-link>
                    </div>
                </div>

                <div v-else class="flex xl:flex-row flex-col gap-[30px] lg:gap-[30px] xl:gap-[70px]">
                    <div class="flex-1 overflow-auto">
                        <table id="cart-table" class="responsive nowrap table-wrapper" style="width:100%">
                            <thead class="table-header">
                                <tr>
                                    <th class="text-lg md:text-xl font-semibold leading-none text-title dark:text-white">Product Info</th>
                                    <th class="text-lg md:text-xl font-semibold leading-none text-title dark:text-white">Price</th>
                                    <th class="text-lg md:text-xl font-semibold leading-none text-title dark:text-white">Quantity</th>
                                    <th class="text-lg md:text-xl font-semibold leading-none text-title dark:text-white">Total</th>
                                    <th class="text-lg md:text-xl font-semibold leading-none text-title dark:text-white">Remove</th>
                                </tr>
                            </thead>
                            <tbody class="table-body">
                                <tr v-for="item in items" :key="item.cart_key">
                                    <td class="w-[42%]">
                                        <div class="flex items-center gap-3 md:gap-4 lg:gap-6 cart-product">
                                            <div class="w-14 sm:w-20 flex-none py-3">
                                                <img :src="item.image || fallbackImage" :alt="item.name">
                                            </div>
                                            <div class="flex-1">
                                                <h6 class="leading-none font-medium">{{ item.category_name || 'Product' }}</h6>
                                                <h5 class="font-semibold leading-none mt-2">
                                                    <router-link :to="item.product_id ? `/product-details/${item.product_id}` : '/product-details'">{{ item.name }}</router-link>
                                                </h5>
                                                <div v-if="item.selected_modifiers.length" class="mt-3 flex flex-col gap-2">
                                                    <div v-for="modifier in item.selected_modifiers" :key="modifier.modifier_id" class="flex items-center gap-2 flex-wrap text-sm text-paragraph dark:text-white-light">
                                                        <span class="font-medium text-title dark:text-white">{{ modifier.modifier_name }}:</span>
                                                        <div class="flex items-center gap-2 flex-wrap">
                                                            <template v-for="option in modifier.options" :key="`${modifier.modifier_id}-${option.option_id || option.label}`">
                                                                <span
                                                                    v-if="modifier.is_color"
                                                                    class="inline-flex w-6 h-6 rounded-full border border-[#E3E5E6] dark:border-white/20"
                                                                    :title="option.label"
                                                                    :style="{ backgroundColor: option.value || '#D4D4D8' }"
                                                                ></span>
                                                                <span v-else class="inline-flex rounded-full bg-white dark:bg-title px-3 py-1 border border-[#E3E5E6] dark:border-white/15">
                                                                    {{ option.label }}
                                                                </span>
                                                            </template>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="flex flex-col gap-2">
                                            <h6 class="text-base md:text-lg leading-none text-title dark:text-white font-semibold">{{ item.formattedUnitPrice }}</h6>
                                            <span v-if="item.modifiers_total" class="text-sm text-paragraph dark:text-white-light">Includes modifiers</span>
                                        </div>
                                    </td>
                                    <td>
                                        <IncDec :model-value="item.quantity" @update:model-value="(value) => updateQuantity(item.cart_key, value)"/>
                                    </td>
                                    <td>
                                        <h6 class="text-base md:text-lg leading-none text-title dark:text-white font-semibold">{{ item.formattedLineTotal }}</h6>
                                    </td>
                                    <td>
                                        <button type="button" class="w-8 h-8 bg-[#E8E9EA] dark:bg-dark-secondary flex items-center justify-center ml-auto duration-300 text-title dark:text-white" @click="removeItem(item.cart_key)">
                                            <svg class="fill-current " width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0.546875 1.70822L1.70481 0.550293L5.98646 4.83195L10.2681 0.550293L11.3991 1.6813L7.11746 5.96295L11.453 10.2985L10.295 11.4564L5.95953 7.12088L1.67788 11.4025L0.546875 10.2715L4.82853 5.98988L0.546875 1.70822Z"/>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <div class="bg-[#FAFAFA] dark:bg-dark-secondary pt-[30px] md:pt-[40px] px-[30px] md:px-[40px] pb-[30px] border border-[#17243026] border-opacity-15 rounded-xl">
                            <div class="text-right flex justify-end flex-col w-full ml-auto mr-0">
                                <div class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium">
                                    <span>Sub Total:</span>
                                    <span>{{ formattedSubtotal }}</span>
                                </div>
                                <div class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium mt-3">
                                    <span>Delivery:</span>
                                    <span>{{ formattedDelivery }}</span>
                                </div>
                            </div>
                            <div class="mt-6 pt-6 border-t border-bdr-clr dark:border-bdr-clr-drk">
                                <div class="flex justify-between flex-wrap font-semibold leading-none text-2xl">
                                    <span>Total:</span>
                                    <span>{{ formattedGrandTotal }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="sm:mt-[10px] py-5 flex items-end gap-3 flex-wrap justify-end">
                            <router-link to="/shop-v2" class="btn btn-sm btn-outline !text-title hover:!text-white before:!z-[-1] dark:!text-white dark:hover:!text-title">
                                Continue Shopping
                            </router-link>
                            <router-link to="/checkout" class="btn btn-sm btn-theme-solid !text-white hover:!text-primary before:!z-[-1]">
                                Checkout
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>
    </div>
</template>

<script setup>
    import { computed, onMounted } from 'vue';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import IncDec from '@/components/inc-dec.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import { PRODUCT_PLACEHOLDER as fallbackImage } from '@/lib/placeholder'
    import Aos from 'aos';
    import { formatPrice } from '@/lib/storefront-api';
    import { useCart } from '@/composables/use-cart';

    const DELIVERY_AMOUNT = 0;

    const cart = useCart();
    const items = cart.items;
    const priceNotice = cart.priceNotice;
    const formattedSubtotal = cart.formattedSubtotal;
    const grandTotal = computed(() => cart.subtotal.value + DELIVERY_AMOUNT);
    const formattedDelivery = computed(() => formatPrice(DELIVERY_AMOUNT));
    const formattedGrandTotal = computed(() => formatPrice(grandTotal.value));

    const updateQuantity = (cartKey, quantity) => {
        cart.updateQuantity(cartKey, quantity);
    };

    const removeItem = (cartKey) => {
        cart.removeItem(cartKey);
    };

    onMounted(async () => {
        Aos.init();
        cart.initializeCart();
        // Bring a cart that has been sitting in this browser back in line with the
        // catalog, since the server prices the order from the catalog.
        await cart.refreshPrices().catch(() => null);
    });
</script>
